import {
  AgxColumn,
  AgxDropdown,
  AgxSaveChangesButton,
  DropdownOption,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import { AgxToast } from '@urbanx/agx-ui-components'
import LoadingState from 'components/loadingState/LoadingState'
import { useCallback, useEffect, useMemo, useState } from 'react'
import TeamPageHeader from '../TeamPageHeader/TeamPageHeader'
import { TeamPageTabs } from '../TeamPageHeader/TeamPageTabs'
import {
  StaffModel,
  useGetStaffByRoles,
  useInvalidateUseGetAllStaff,
} from '@urbanx/agx.staff.client'
import {
  useGetUrbanXTeamForAgency,
  useInvalidateUseGetUrbanXTeamForAgency,
  useInvalidateUseGetStaffMembersAssignedToAgencies,
  useSetUrbanXTeamForAgency,
  UrbanXTeam,
  UrbanXTeamDto,
} from '@urbanx/agx.agencies.client'
import useAgencyIdParam from 'hooks/useAgencyIdParam'
import Panel from 'components/panel/Panel'
import { StaffRole } from '@urbanx/agx.auth.client'
import { useQuery } from '@tanstack/react-query'
import { isDirtyEqual } from 'utils/compares'
import { AgxToastState } from 'types/commonTypes'

const defaultOption: DropdownOption = { value: '', label: 'None' }

const mapUrbanXTeamDtoToForm = (
  urbanXTeamForAgency?: UrbanXTeamDto
): UrbanXTeam => ({
  businessManagerId: urbanXTeamForAgency?.businessManager?.email ?? '',
  adminManagerId: urbanXTeamForAgency?.adminManager?.email ?? '',
  marketingExpertId: urbanXTeamForAgency?.marketingExpert?.email ?? '',
})

const mapUrbanXTeamToForm = (urbanXTeam?: UrbanXTeam): UrbanXTeam => ({
  businessManagerId: urbanXTeam?.businessManagerId ?? '',
  adminManagerId: urbanXTeam?.adminManagerId ?? '',
  marketingExpertId: urbanXTeam?.marketingExpertId ?? '',
})

const GetStaffOptionsByRole = (
  role: StaffRole,
  staffMembers: StaffModel[]
): DropdownOption[] => {
  if (!staffMembers) return []

  const staffMembersForRole = staffMembers.filter((s) =>
    s.roles?.includes(role)
  )

  const options = staffMembersForRole.map((s) => ({
    value: s.email ?? '',
    label: `${s.firstName} ${s.lastName}`,
  }))

  return [defaultOption, ...options]
}

const ManageUrbanXTeamPage = () => {
  const agencyId = useAgencyIdParam()

  const { data: urbanXTeamForAgency, isPending: isUrbanXTeamForAgencyPending } =
    useGetUrbanXTeamForAgency(agencyId ?? '', !!agencyId)
  const invalidateUrbanXTeamForAgency = useInvalidateUseGetUrbanXTeamForAgency()
  const invalidateGetAllStaff = useInvalidateUseGetAllStaff()
  const invalidateGetStaffMembersAssignedToAgencies =
    useInvalidateUseGetStaffMembersAssignedToAgencies()

  const { getStaffByRoles, getStaffByRolesPending } = useGetStaffByRoles({
    onSuccess: () => {},
    onError: (error) => console.error('Failed to fetch staff:', error),
  })

  const { data: staffByRoles, isPending: isRolesPending } = useQuery<
    StaffModel[]
  >({
    queryKey: ['getStaffByRolesLoader'],
    queryFn: async () => {
      const rolesToFetch = [
        StaffRole.BusinessManager,
        StaffRole.AdminManager,
        StaffRole.MarketingExpert,
      ]
      return await getStaffByRoles({ roles: rolesToFetch })
    },
  })

  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })

  const { setUrbanXTeamForAgency, setUrbanXTeamForAgencyPending } =
    useSetUrbanXTeamForAgency({
      onSuccess: async (_, inputData) => {
        setInitialForm(mapUrbanXTeamToForm(inputData.urbanXTeam))
        setSaveButtonState(SAVE_BUTTON_STATES.SAVED)
        updateToastState({
          color: 'success',
          message: 'Changes Saved',
          open: true,
        })

        if (inputData.agencyId) {
          const currentAgencyId = inputData.agencyId
          await invalidateUrbanXTeamForAgency(currentAgencyId)
          await Promise.all(
            [
              StaffRole.BusinessManager,
              StaffRole.AdminManager,
              StaffRole.MarketingExpert,
            ].map((role) =>
              invalidateGetStaffMembersAssignedToAgencies(role, [currentAgencyId])
            )
          )
        }
        await invalidateGetAllStaff()
      },
      onError: (error) => {
        console.error('Failed to set UrbanX team:', error)
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        updateToastState({
          color: 'error',
          message: 'Error saving changes',
          open: true,
        })
      },
    })

  const [form, updateForm] = useState<UrbanXTeam>(() =>
    mapUrbanXTeamDtoToForm(urbanXTeamForAgency)
  )
  const [initialForm, setInitialForm] = useState<UrbanXTeam>(() =>
    mapUrbanXTeamDtoToForm(urbanXTeamForAgency)
  )

  useEffect(() => {
    if (!urbanXTeamForAgency) return

    const mappedForm = mapUrbanXTeamDtoToForm(urbanXTeamForAgency)
    updateForm(mappedForm)
    setInitialForm(mappedForm)
  }, [urbanXTeamForAgency])

  const isFormDirty = useMemo(() => {
    return !isDirtyEqual(form, initialForm)
  }, [form, initialForm])

  const saveChangesState = useMemo(() => {
    if (setUrbanXTeamForAgencyPending) return SAVE_BUTTON_STATES.SAVING
    if (saveButtonState === SAVE_BUTTON_STATES.ERROR)
      return SAVE_BUTTON_STATES.ERROR
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED
    return saveButtonState
  }, [isFormDirty, saveButtonState, setUrbanXTeamForAgencyPending])

  useEffect(() => {
    if (isFormDirty) {
      setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)
    }
  }, [isFormDirty])

  const isAnythingLoading = useMemo(() => {
    return (
      getStaffByRolesPending || isUrbanXTeamForAgencyPending || isRolesPending
    )
  }, [getStaffByRolesPending, isUrbanXTeamForAgencyPending, isRolesPending])

  const { businessManagers, adminManagers, marketingExperts } = useMemo(() => {
    return {
      businessManagers: GetStaffOptionsByRole(
        StaffRole.BusinessManager,
        staffByRoles ?? []
      ),
      adminManagers: GetStaffOptionsByRole(
        StaffRole.AdminManager,
        staffByRoles ?? []
      ),
      marketingExperts: GetStaffOptionsByRole(
        StaffRole.MarketingExpert,
        staffByRoles ?? []
      ),
    }
  }, [staffByRoles])

  const onSaveClicked = useCallback(async () => {
    if (!form || !isFormDirty || setUrbanXTeamForAgencyPending) return

    await setUrbanXTeamForAgency({ agencyId, urbanXTeam: form })
  }, [
    setUrbanXTeamForAgency,
    agencyId,
    form,
    isFormDirty,
    setUrbanXTeamForAgencyPending,
  ])

  return (
    <AgxColumn fill gap="spacing-xl">
      <AgxToast selector="#agxToast" toastState={toastState} />

      <TeamPageHeader
        selectedTab={TeamPageTabs.UrbanXTeam}
        actionButtons={
          <AgxSaveChangesButton
            state={saveChangesState}
            id={'saveUrbanXTeamButton'}
            text={'Save Changes'}
            variant={'primary'}
            onClick={onSaveClicked}
            disabled={!isFormDirty || setUrbanXTeamForAgencyPending}
          />
        }
      />

      {!isAnythingLoading && (
        <Panel>
          <AgxColumn fill gap="spacing-3xl">
            <AgxDropdown
              id="bm-select"
              hideOptionalText
              label="Business Manager"
              options={businessManagers}
              selected={form?.businessManagerId ?? ''}
              onChange={(value) =>
                updateForm((prev) => ({ ...prev, businessManagerId: value }))
              }
            />

            <AgxDropdown
              id="am-select"
              hideOptionalText
              label="Admin Manager"
              options={adminManagers}
              selected={form?.adminManagerId ?? ''}
              onChange={(value) =>
                updateForm((prev) => ({ ...prev, adminManagerId: value }))
              }
            />

            <AgxDropdown
              id="me-select"
              hideOptionalText
              label="Marketing Expert"
              options={marketingExperts}
              selected={form?.marketingExpertId ?? ''}
              onChange={(value) =>
                updateForm((prev) => ({ ...prev, marketingExpertId: value }))
              }
            />
          </AgxColumn>
        </Panel>
      )}

      {isAnythingLoading && <LoadingState />}
    </AgxColumn>
  )
}

export default ManageUrbanXTeamPage
